import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'

import Button from '../Button'
import { ReactComponent as SyncedIcon } from '../../assets/icons/sync-done-icon.svg'
import { ReactComponent as UnsyncedIcon } from '../../assets/icons/sync-queued-icon.svg'
import { ReactComponent as PreOrderIcon } from '../../assets/icons/tabs/pre-order-icon.svg'
import { ReactComponent as DayOfEventMenuIcon } from '../../assets/icons//tabs/doe-menu-icon.svg'
import { ReactComponent as CoffeeMenuIcon } from '../../assets/icons/tabs/coffee-menu-icon.svg'
import { ReactComponent as CordialMenuIcon } from '../../assets/icons/tabs/cordial-menu-icon.svg'
import { ReactComponent as DessertMenuIcon } from '../../assets/icons/tabs/dessert-menu-icon.svg'
import { ReactComponent as RestockMenuIcon } from '../../assets/icons/tabs/restock-menu-icon.svg'
import { ReactComponent as ErrorIcon } from '../../assets/icons/red-exclamation.svg'

import styles from './TabOrderRow.module.scss'
import { scrollToTableRow } from '../../utils/search'
import { paymentIcons } from '../../utils/icons'

const eventHandler = (callback, event) => {
  event.stopPropagation()
  callback(event)
}

const TabOrderRow = ({
  amount,
  className,
  creditCardType,
  focusedItemId,
  highlightedIds,
  isSuites,
  menuSubtype,
  modifiedAt,
  onCloseOrder,
  onSelectOrder,
  onViewOrder,
  orderId,
  orderNumber,
  orderStatus,
  orderType,
  payment,
  searchQuery,
  syncStatus,
  theme,
  userNotes,
  errorMessage,
  isMultiPayment,
  ...props
}) => {
  const rowRef = useRef()
  const isMobilePay = payment.toLowerCase() === 'mobile pay'
  const unsyncedError = syncStatus.toLowerCase() !== 'synced' && errorMessage
  const syncStatusClassname = cn(styles.syncStatus, styles[isMobilePay ? 'closed' : orderStatus])
  const orderStatusClassname = cn(styles.orderStatus, styles[isMobilePay ? 'closed' : orderStatus])
  let classNames = cn(styles.tabOrderRow, styles[theme], styles[isMobilePay ? 'closed' : orderStatus], unsyncedError && styles.addRedText, className)
  let orderStatusElement = <Button disabled={unsyncedError} className={styles.closeOrderButton} onClick={eventHandler.bind(this, () => onCloseOrder(orderId))}>close order</Button>
  let syncedIconElement = <SyncedIcon className={styles.syncStatusIcon} />

  const menuIcons = {
    day_of_event: <DayOfEventMenuIcon className={styles.tabOrderIcon} />,
    coffee: <CoffeeMenuIcon className={styles.tabOrderIcon} />,
    cordial: <CordialMenuIcon className={styles.tabOrderIcon} />,
    dessert: <DessertMenuIcon className={styles.tabOrderIcon} />,
    preorder: <PreOrderIcon className={styles.tabOrderIcon} />,
    restock: <RestockMenuIcon className={styles.tabOrderIcon} />,
  }

  let tabOrderIcon
  if (isSuites) {
    tabOrderIcon = menuIcons[orderType]
  } else {
    let status = orderStatus === 'closed' ? 'closed' : 'open'
    tabOrderIcon = paymentIcons[creditCardType ?? payment]?.[status]

    if (payment === 'Card Declined') {
      tabOrderIcon = paymentIcons['Card Declined'][status]
    }

    if (isMultiPayment) {
      tabOrderIcon = paymentIcons['Split Tender'][status]
      payment = 'Split'
    }
  }

  
  if (orderStatus === 'closed') {
    orderStatusElement = <span className={orderStatusClassname}>{orderStatus}</span>
  }

  // NOTE (@westinschepper): This is temporary
  if (isMobilePay) {
    orderStatusElement = <span className={orderStatusClassname}>closed</span>
  }

  if (syncStatus !== 'synced') {
    syncedIconElement = <UnsyncedIcon className={styles.syncStatusIcon} />
  }

  if (unsyncedError) {
    syncedIconElement = <ErrorIcon className={styles.syncStatusIcon} />
  }

  let style = {}
  let isFocused = false
  if (searchQuery) {
    if (focusedItemId === orderId) {
      isFocused = true
    } else if (!highlightedIds.includes(orderId)) {
      style.opacity = '.1'
    }
  }

  if(isFocused && rowRef.current) {
    scrollToTableRow(rowRef.current)
  }

  let rowDetails = ''
  if (payment && userNotes) {
    rowDetails =  `${payment} • ${userNotes}`
  } else if (payment) {
    rowDetails = payment
  } else if (userNotes) {
    rowDetails = userNotes
  }

  return (
    <div
      {...props}
      className={classNames}
      onClick={eventHandler.bind(this, () => onViewOrder(orderId))}
      style={style}
      ref={rowRef}
    >
      <span className={styles.payment}>
        <div className={styles.tabOrderIcon}>
          {tabOrderIcon}
        </div>
        <div className={styles.rowDetails}>
          {rowDetails}
        </div>
      </span>
      <span className={styles.amount}>{amount}</span>
      <span className={styles.modifiedAt}>{modifiedAt}</span>
      <span className={syncStatusClassname}>{syncedIconElement}{orderNumber}</span>
      <div className={styles.actions}>
        {orderStatusElement}
        <Button className={[styles.detailsButton, unsyncedError && styles.addRedBackground]} onClick={eventHandler.bind(this, () => onViewOrder(orderId))}>details</Button>
      </div>
    </div>
  )
}

TabOrderRow.defaultProps = {
  orderId: '',
  payment: '',
  amount: '',
  modifiedAt: '',
  orderNumber: '',
  syncStatus: 'unscyned',
  orderStatus: 'open',
  theme: 'dark',
  orderType: '',
  onSelectOrder: () => {},
  onCloseOrder: () => {},
  onViewOrder: () => {},
}

TabOrderRow.propTypes = {
  orderId: PropTypes.string,
  payment: PropTypes.string,
  amount: PropTypes.string,
  modifiedAt: PropTypes.string,
  orderNumber: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
  syncStatus: PropTypes.oneOf(['synced', 'unsynced']),
  orderStatus: PropTypes.oneOf(['open', 'closed']),
  theme: PropTypes.oneOf(['light', 'dark']),
  orderType: PropTypes.string,
  onSelectOrder: PropTypes.func,
  onCloseOrder: PropTypes.func,
  onViewOrder: PropTypes.func,
}

export default TabOrderRow
